/// <amd-module name="Tfs/Frame/Components/Navigation/TopLevelNavigation" />
import * as React from "react";
import * as Resources from "Tfs/Frame/Resources";
import { TeamProjectCollectionReference } from "Tfs/Platform/Contracts/Core";
import { ProjectPersona } from "Tfs/Platform/ProjectPersona";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { activateMapping, queueSyncUserAccountMapping } from "VSS/Features/Services/AccountMappingService";
import {
    getMostRecentlyAccessedUserOrganizations,
    getNewOrganizationUrl,
    getUserOrganizations,
    IOrganization,
    isUserAccountMappingRequired
} from "VSS/Features/Services/OrganizationsService";
import { TeamFoundationHostType } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, useComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getServiceLocation, routeUrl } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssSecurityService } from "VSS/Platform/Security";
import { combineUrlPaths, Uri } from "VSS/Platform/Util/Url";
import { Callout } from "VSSUI/Callout";
import { ExpandableContainer, IExpandable } from "VSSUI/Expandable";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { css, KeyCode } from "VSSUI/Util";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";
import { useFlyoutNavigation } from "../../Util/Navigation";
import { SuiteLogo } from "../SuiteLogo/SuiteLogo";
import { SwitchToDesktopButton } from "./SwitchToDesktopButton";

interface IHost {
    id: string;
    name: string;
    url: string;
    avatarUrl: string;
}

interface ITopLevelNavigationState {
    collections: ObservableArray<IHost>;
    newOrgLink: ObservableValue<string | undefined>;
    organizations: ObservableArray<IOrganization>;
}

interface ITopLevelNavigationProps {
    className?: string;
    showAlways?: boolean;
}

const AccountMappingWhenOrgDiscrepancyFF = "VisualStudio.Services.AdminEngagement.OrganizationNavigation.AccountMappingWhenOrgDiscrepancy";
const EditableOrganizationAvatarFF = "VisualStudio.Services.AdminEngagement.OrganizationOverview.EditableOrganizationAvatar";

class TopLevelNavigation extends VssComponent<ITopLevelNavigationProps, ITopLevelNavigationState> {
    private mruOrganizations: IOrganization[];
    private allOrganizations: IOrganization[];
    private showMoreLink = true;
    private isHosted = true;
    private settingsLink: string;
    private accessLevelsLink: string;
    private displayedHostId: string;

    constructor(props: ITopLevelNavigationProps, context: IVssComponentContext) {
        super(props, context);

        const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = vssPageService.getData()!;

        this.displayedHostId = pageData.hostId;
        this.isHosted = pageData.isHosted;

        // For OnPrem deployments we need the AccessLevels link.
        if (!this.isHosted) {
            const securityService = context.pageContext.getService<IVssSecurityService>("IVssSecurityService");
            const hasFrameworkWritePermission = securityService.hasPermission("1f4179b3-6bac-4d01-b421-71ea09171400", "FrameworkGlobalSecurity", 2);

            if (hasFrameworkWritePermission) {
                const rootUrl = getServiceLocation(context.pageContext, undefined, TeamFoundationHostType.Deployment);
                this.accessLevelsLink = routeUrl(context.pageContext, "ms.vss-admin-web.admin-access-levels-route", {}, rootUrl);
            }
        }

        this.settingsLink = routeUrl(context.pageContext, "ms.vss-admin-web.collection-admin-hub-route");

        this.state = {
            collections: new ObservableArray<IHost>([]),
            newOrgLink: new ObservableValue<string | undefined>(undefined),
            organizations: new ObservableArray<IOrganization>([]),
        };

        if (this.isHosted) {
            this.trackPromise(
                Promise.all([getMostRecentlyAccessedUserOrganizations(context.pageContext), getUserOrganizations(context.pageContext)])
            ).promise.then((results) => {
                this.mruOrganizations = results[0];
                this.allOrganizations = results[1];

                this.updateOrganizationList();
            });
            this.trackPromise(getNewOrganizationUrl(context.pageContext)).promise.then(this.updateNewOrgUrl);
        } else {
            const coreRestClient = context.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient", {
                hostType: TeamFoundationHostType.Deployment
            });

            this.trackPromise(coreRestClient.getProjectCollections(1000 /* top */)).promise.then(this.updateCollectionList);
        }
    }

    public componentDidMount() {
        if (this.isHosted) {
          this._queueSyncUserAccountMapping();
        }
    }

    public render(): JSX.Element {
        return (
            <ScreenSizeConditional condition={(screenSize) => this.props.showAlways || !useFlyoutNavigation(this.context.pageContext, screenSize)}>
                <div
                    className={css(this.props.className, "top-level-navigation region-navigation flex-column flex-noshrink scroll-hidden expanded")}
                    role="navigation"
                >
                    <div className="top-level-section scroll-hidden flex-column flex-grow">
                        <Observer hosts={this.isHosted ? this.state.organizations : this.state.collections}>
                            {(props: { hosts: IHost[] }) =>
                                props.hosts.length ? (
                                    <FocusZone
                                        direction={FocusZoneDirection.Vertical}
                                        focusGroupProps={{ defaultElementId: "host-" + props.hosts[0].id }}
                                    >
                                        <div className="flex-column v-scroll-auto h-scroll-hidden custom-scrollbar" role="listbox" aria-label={Resources.MyOrgs}>
                                            {props.hosts.map(this.renderListItem)}
                                        </div>
                                        {this.isHosted && (
                                            <div className="action-section flex-column flex-noshrink">
                                                {this.allOrganizations && this.allOrganizations.length - props.hosts.length > 0 ? (
                                                    <Link
                                                        className="action-link top-navigation-item focus-treatment flex-row flex-noshrink"
                                                        onClick={this.showAllHostsClicked}
                                                    >
                                                        {this.allOrganizations.length - props.hosts.length === 1
                                                            ? Resources.OneMoreOrganization
                                                            : format(Resources.MoreOrganizations, this.allOrganizations.length - props.hosts.length)}
                                                    </Link>
                                                ) : null}
                                                <Observer newOrgUrl={this.state.newOrgLink}>
                                                    {(props: { newOrgUrl: string | undefined }) =>
                                                        props.newOrgUrl ? (
                                                            <Link
                                                                className="action-link top-navigation-item focus-treatment flex-row flex-noshrink"
                                                                href={props.newOrgUrl}
                                                                id="new-org-link"
                                                            >
                                                                {Resources.NewOrg}
                                                            </Link>
                                                        ) : null
                                                    }
                                                </Observer>
                                            </div>
                                        )}
                                    </FocusZone>
                                ) : null
                            }
                        </Observer>
                    </div>
                    <div className="announcements-section flex-column flex-noshrink">
                        <WrappedComponent wrappedType="globalAnnouncement" dependencies={["ms.vss-tfs-web.suite-me-page-content"]} />
                    </div>
                    {!this.isHosted ? (
                        <div className="related-pages-section flex-column flex-noshrink">
                            <span className="section-header">{Resources.RelatedPages}</span>
                            {this.isHosted && (
                                <span className="related-link">
                                    <Link className="link-text" href="https://docs.microsoft.com/en-us/azure/devops/release-notes/">
                                        {Resources.WhatsNew}
                                    </Link>
                                </span>
                            )}
                            <span className="related-link">
                                <Link className="link-text" href="https://docs.microsoft.com/en-us/azure/devops/?view=azdevops">
                                    {Resources.Documentation}
                                </Link>
                            </span>
                            <span className="related-link">
                                <Link className="link-text" href="https://azure.microsoft.com/en-us/support/devops/">
                                    {Resources.GetHelp}
                                </Link>
                            </span>
                            {this.accessLevelsLink && (
                                <span className="related-link">
                                    <Link className="link-text" href={this.accessLevelsLink}>
                                        {Resources.AccessLevels}
                                    </Link>
                                </span>
                            )}
                        </div>
                    ) : null}
                    <div className="navigation-footer">
                        <FPSLink
                            className="action-link navigation-link focus-treatment top-navigation-item flex-row"
                            href={this.settingsLink}
                            id="settings-link"
                            removeUnderline={true}
                        >
                            <Icon className="icon-margin" iconName="Settings" size={IconSize.medium} />
                            <span className="text-ellipsis">{this.isHosted ? Resources.OrgSettings : Resources.CollectionSettings}</span>
                        </FPSLink>
                        <SwitchToDesktopButton
                            className="action-link top-navigation-item"
                            context={this.context}
                            iconClassName="icon-margin"
                            textClassName="text-ellipsis"
                        />
                    </div>
                </div>
            </ScreenSizeConditional>
        );
    }

    private renderListItem = (host: IHost) => {
        const id = "host-" + host.id;
        let avatarUrl = isFeatureFlagEnabled(this.context.pageContext, EditableOrganizationAvatarFF, false) && host.avatarUrl ? host.avatarUrl : undefined;

        return (
            <Link
                ariaSelected={host.id === this.displayedHostId}
                className={css(
                    "host-link navigation-link top-navigation-item focus-treatment flex-row flex-center flex-noshrink relative",
                    host.id === this.displayedHostId && "displayed"
                )}
                href={host.url}
                id={id}
                key={id}
                role="option"
                removeUnderline={true}
            >
                <ProjectPersona displayName={host.name} size="small" url={avatarUrl} />
                <span className="text-ellipsis">{host.name}</span>
            </Link>
        );
    };

    private updateCollectionList = (collections: TeamProjectCollectionReference[]) => {
        const rootUrl = getServiceLocation(this.context.pageContext, undefined, TeamFoundationHostType.Deployment);

        const hosts = collections.map((collection: TeamProjectCollectionReference, index: number) => {
            return {
                id: collection.id,
                name: collection.name,
                url: combineUrlPaths(rootUrl, encodeURIComponent(collection.name)),
                avatarUrl: collection.avatarUrl
            };
        });
        hosts.sort((h1, h2) => {
            if (h1.id === this.displayedHostId) {
                return -1;
            } else if (h2.id === this.displayedHostId) {
                return 1;
            } else {
                return localeIgnoreCaseComparer(h1.name, h2.name);
            }
        });

        this.state.collections.push(...hosts);
    };

    private updateNewOrgUrl = (url: string | undefined) => {
        if (url) {
            const uri = new Uri(url);
            uri.addQueryParam("campaign", "o~msft~vsts~orghome", true);
            this.state.newOrgLink.value = uri.absoluteUri;
        }
    };

    private showAllHostsClicked = () => {
        this.showMoreLink = false;
        this.updateOrganizationList();
    };

    // Figure out which organizations to display.
    // Order will be: current org, followed by MRU, followed by rest of orgs in alphabetical order
    private updateOrganizationList = () => {
        // Default to all organizations
        let organizationsToDisplay = this.allOrganizations;

        // If we have an mru, that will appear on top
        if (this.mruOrganizations) {
            // keep track of organizaion allready processed in case they are in both lists.
            const processedOrganizations: string[] = [];
            let foundCurrentOrg = false;
            organizationsToDisplay = [];
            for (const organization of [...this.mruOrganizations, ...this.allOrganizations]) {
                if (processedOrganizations.indexOf(organization.id) < 0) {
                    processedOrganizations.push(organization.id);

                    // If hsot is the current host then push on front of array
                    if (organization.id === this.displayedHostId) {
                        organizationsToDisplay.unshift(organization);
                        foundCurrentOrg = true;
                    } else {
                        organizationsToDisplay.push(organization);
                    }

                    if (foundCurrentOrg && this.showMoreLink && organizationsToDisplay.length >= 4) {
                        break;
                    }
                }
            }
        }

        const itemsToDisplay = this.showMoreLink && organizationsToDisplay.length > 4 ? organizationsToDisplay.slice(0, 4) : organizationsToDisplay;
        this.state.organizations.splice(0, this.state.organizations.length, ...itemsToDisplay);
    };

    private async _queueSyncUserAccountMapping() {
        const isSyncRequired: Boolean = await isUserAccountMappingRequired(this.context.pageContext);

        if (isSyncRequired) {
            await queueSyncUserAccountMapping(this.context.pageContext);

            if (isFeatureFlagEnabled(this.context.pageContext, AccountMappingWhenOrgDiscrepancyFF, false)) {
                const vssPageService = await this.context.pageContext.getService<IVssPageService>("IVssPageService");
                const vssPageData = await vssPageService.getData();

                if (vssPageData) {
                    await activateMapping(this.context.pageContext, vssPageData.user.descriptor, vssPageData.hostId);
                }
            }
        }
    }
}

Components.add("ms.vss-tfs-web.frame.top-level-navigation", TopLevelNavigation);

const TopLevelNavigationHeaderButton = () => {
    const pageContext = useComponentContext().pageContext;

    const renderCallout = (expandable: IExpandable): JSX.Element => {
        return (
            <Callout className="absolute-fill" contentClassName="flex-column" lightDismiss={true} modal={true} onDismiss={expandable.collapse}>
                <div className="region-header flex-row flex-noshrink">
                    <SuiteLogo alwaysExpanded={true} />
                </div>
                <TopLevelNavigation className="flex-grow" showAlways={true} />
            </Callout>
        );
    };

    return (
        <ScreenSizeConditional condition={(screenSize) => useFlyoutNavigation(pageContext, screenSize)}>
            <ExpandableContainer
                className="navigation-header-button commandbar-item commandbar-icon flex-noshrink justify-center cursor-pointer"
                renderCallout={renderCallout}
                tabIndex={0}
                expandKey={[KeyCode.enter, KeyCode.space]}
                ariaLabel={Resources.NavigationMenu}
            >
                <Icon iconName="GlobalNavButton" size={IconSize.medium} />
            </ExpandableContainer>
        </ScreenSizeConditional>
    );
};

VssComponent.register("ms.vss-tfs-web.frame.top-navigation-header-button", TopLevelNavigationHeaderButton);
